import {
  Component,
  input,
  output,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  computed,
  inject,
  LOCALE_ID,
  signal,
} from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { ChartModule } from '@progress/kendo-angular-charts';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { FormsModule } from '@angular/forms';
import { SVGIcon, moreVerticalIcon, pencilIcon } from '@progress/kendo-svg-icons';
import DataTable from 'datatables.net-dt';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { formatDateRangeLabel, toCalendarDay } from 'src/app/core/shared/common/date-range.util';
import { environment } from 'src/environments/environment';
import { MttmAnalysisResponse, SLATargetsResponse } from '../../../../../../../core/models/analytics/vss-models';

/** Plain text cells: DataTables writes raw HTML, and render.text() is its own escaper. */
const text = DataTable.render.text();

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-mttm-analysis',
  templateUrl: './mttm-analysis.component.html',
  styleUrls: ['./mttm-analysis.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ChartModule, ButtonsModule, DialogsModule, FormsModule, DatatableComponent],
})
export class MttmAnalysisComponent {
  private locale = inject(LOCALE_ID);

  mttmAnalysisResponse = input.required<MttmAnalysisResponse>();
  slaTargetsResponse = input.required<SLATargetsResponse>();

  public organizationId = input<number | null>();
  public startDate = input<string | null>();
  public endDate = input<string | null>();

  onSLAEdit = output<{ type: 'critical' | 'high' | 'both' }>();
  onSLASave = output<{ critical: number; high: number }>();
  onSLACancel = output<{ type: 'critical' | 'high' | 'both' }>();

  protected readonly pencilIcon = pencilIcon;
  protected readonly moreVerticalIcon: SVGIcon = moreVerticalIcon;
  chartData = computed(() => {
    return this.mttmAnalysisResponse().mttm_trend.map((trend) => ({
      ...trend,
      date: toCalendarDay(trend.date),
    }));
  });

  public showPopup = signal<boolean>(false);

  public modalTitle = computed((): string => {
    const start = this.startDate();
    const end = this.endDate();
    const label = start && end ? formatDateRangeLabel([start, end]) : '';
    return label ? `Critical & High MTTM (${label})` : 'Critical & High MTTM';
  });

  public datatableUrl = computed((): string => {
    const id = this.organizationId();
    return id != null
      ? `${environment.apiUrl}/v1/vicarius/analytics/criticalandhighmttmanalysisdetails(${id})`
      : '';
  });

  /** The same period the card was computed for. */
  public periodParams = computed((): Record<string, string> => {
    const start = this.startDate();
    const end = this.endDate();
    return start && end
      ? { current_start: encodeURIComponent(start), current_end: encodeURIComponent(end) }
      : {};
  });

  private date = (value: string) => (value ? formatDate(value, 'MMM d, y, h:mm:ss a', this.locale) : '');

  /** The trend period is a UTC day: in local time it would show the day before. */
  private day = (value: string) => (value ? formatDate(value, 'MMM d, y', this.locale, 'UTC') : '');

  public datatableColumns = [
    { data: 'endpoint_name', name: 'endpoint_name', title: 'Endpoint | Device Name', type: 'string', render: text },
    { data: 'agent_id', name: 'agent_id', title: 'Endpoint ID', type: 'string', render: text },
    { data: 'cve_uid', name: 'cve_uid', title: 'CVE', type: 'string', render: text },
    { data: 'severity', name: 'severity', title: 'Severity', type: 'string', render: text },
    { data: 'detected_at', name: 'detected_at', title: 'Detected At', type: 'string', render: this.date },
    { data: 'mitigated_at', name: 'mitigated_at', title: 'Mitigated At', type: 'string', render: this.date },
    {
      data: 'mttm_days',
      name: 'mttm_days',
      title: 'MTTM',
      type: 'num',
      render: (value: number) => (value != null ? `${value.toFixed(2)} days` : ''),
    },
    { data: 'bucket_date', name: 'bucket_date', title: 'Trend Period', type: 'string', render: this.day },
  ];

  onViewData(): void {
    this.showPopup.set(true);
  }

  close(): void {
    this.showPopup.set(false);
  }
}
